import React, { useCallback, useContext, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import {
  NEW_ORGANIZATION,
  type NewOrganizationData,
  type NewOrganizationVariables,
} from "../CustomerProvisioningApi";
import { showToast } from "../../Forms/Dialogs/Toast/Toast";
import {
  TextButton,
  TextInput,
  useMutationWrapper,
  ModalContext,
  ModalButtonsContainer,
  showErrorDialog,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";

interface NewOrganizationFormValues {
  name: string;
  slug: string;
  primaryDomain: string;
}

const NewOrganization: React.FC = () => {
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    setValue,
  } = useForm<NewOrganizationFormValues>({ mode: "all" });

  const [createNewOrganization] = useMutationWrapper<
    NewOrganizationData,
    NewOrganizationVariables
  >(NEW_ORGANIZATION, {
    refetchQueries: ["cnc_organizations"],
  });
  const { closeModal } = useContext(ModalContext);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const validateSlug = useCallback((slug: string) => {
    if (!slug) {
      return "Missing slug";
    }
    if (slug.length < 2) {
      return "Slug must be at least 2 characters long";
    }
    if (/[^a-z0-9_]+/.test(slug)) {
      return "Slug cannot contain spaces or special characters";
    }
    if (slug.toLowerCase() !== slug) {
      return "Slug must be lowercase";
    }
    return true;
  }, []);

  const onSubmit = useCallback(
    (data: NewOrganizationFormValues) => {
      setIsSubmittingForm(true);

      createNewOrganization({
        variables: {
          slug: data.slug,
          name: data.name,
          primary_domain: data.primaryDomain,
        },
      })
        .then((res) => {
          setIsSubmittingForm(false);
          if (!!res.data?.cnc_new_organization?.ok) {
            closeModal();
            showToast({
              message: res.data?.cnc_new_organization?.msg,
            });
          } else {
            showErrorDialog({ message: res.data?.cnc_new_organization?.msg });
          }
        })
        .catch((err: { message?: string }) => {
          setIsSubmittingForm(false);
          showErrorDialog({ message: err.message });
        });
    },
    [createNewOrganization, closeModal]
  );

  const setSlugAccordingToName = useCallback(
    (name: string) => {
      const slug = name
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");
      setValue("slug", slug);
    },
    [setValue]
  );

  return (
    <div>
      <form
        className="create-org-account-modal"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name={"name"}
          render={({ field: { value, onChange, onBlur } }) => {
            return (
              <TextInput
                type={"text"}
                value={value}
                data-testid={"name"}
                labelTop
                onBlur={onBlur}
                name={"name"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                  setSlugAccordingToName(e.target.value);
                }}
                label={"Name"}
                required
                placeholder={"ACME Corp"}
                error={errors.name ? errors.name : null}
              />
            );
          }}
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
          }}
        />
        <Controller
          name={"slug"}
          render={({ field: { value, onChange, onBlur } }) => {
            return (
              <TextInput
                type={"text"}
                value={value}
                data-testid={"slug"}
                labelTop
                onBlur={onBlur}
                name={"slug"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                }}
                label={"Slug"}
                placeholder={"An identifier for the organization"}
                error={errors.slug ? errors.slug : null}
                required
              />
            );
          }}
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
            validate: validateSlug,
          }}
        />
        <Controller
          name={"primaryDomain"}
          render={({ field: { value, onChange, onBlur } }) => {
            return (
              <TextInput
                type={"text"}
                value={value}
                data-testid={"primary_domain"}
                labelTop
                onBlur={onBlur}
                name={"primaryDomain"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                }}
                label={"Primary Domain"}
                placeholder={"acme.com"}
                error={errors.primaryDomain ? errors.primaryDomain : null}
                required
              />
            );
          }}
          control={control}
          rules={{
            required: REQUIRED_ERROR_MESSAGE,
          }}
        />
        <ModalButtonsContainer>
          <TextButton
            type="button"
            data-testid={"create-tenant-cancel"}
            label={"Cancel"}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              closeModal();
            }}
          />
          <TextButton
            type="submit"
            label={"Create Organization"}
            data-testid={"create-organization"}
            buttonStyle={"primary"}
            isSubmitting={isSubmittingForm}
            rightIcon={isSubmittingForm ? "seem-icon-spinner spinner" : ""}
            disabled={!isValid || !isDirty || isSubmittingForm}
          />
        </ModalButtonsContainer>
      </form>
    </div>
  );
};
export default NewOrganization;
